import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { WindowRefService } from './window-ref.service';

@Injectable({
  providedIn: 'root',
})
export class GmapLoaderService {
  private isLoaded = false;
  private readonly scriptSelector = 'script[src*="maps.googleapis.com/maps/api/js"]';

  constructor(private window: WindowRefService) {}

  load(): Observable<boolean> {
    return new Observable<boolean>((observer: Observer<boolean>) => {
      if (this.isLoaded || (this.window.nativeWindow.google && this.window.nativeWindow.google.maps)) {
        observer.next(true);
        observer.complete();
      } else {
        const apiKey = this.getApiKey();
        if (!apiKey) {
          observer.error('Не удалось загрузить Google Карты: отсутствует API ключ');
          return;
        }

        const sharedLoader = this.window.nativeWindow.loadGoogleMaps;
        if (sharedLoader) {
          sharedLoader(apiKey).then(() => {
            this.isLoaded = true;
            observer.next(true);
            observer.complete();
          }).catch(error => observer.error(error));
          return;
        }

        const existingScript = document.querySelector(this.scriptSelector) as HTMLScriptElement | null;
        if (existingScript) {
          if (this.window.nativeWindow.google && this.window.nativeWindow.google.maps) {
            this.isLoaded = true;
            observer.next(true);
            observer.complete();
            return;
          }

          existingScript.addEventListener('load', () => {
            this.isLoaded = !!(this.window.nativeWindow.google && this.window.nativeWindow.google.maps);
            observer.next(this.isLoaded);
            observer.complete();
          }, { once: true });

          existingScript.addEventListener('error', () => {
            existingScript.remove();
            observer.error('Не удалось загрузить Google Карты');
          }, { once: true });
          return;
        }

        const nodeScript = document.createElement('script');
        nodeScript.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
        nodeScript.type = 'text/javascript';
        nodeScript.async = true;
        nodeScript.defer = true;
        nodeScript.setAttribute('data-google-maps-loader', '1');
        nodeScript.onload = () => {
          this.isLoaded = true;
          observer.next(true);
          observer.complete();
        };
        nodeScript.onerror = () => {
          nodeScript.remove();
          observer.error('Не удалось загрузить Google Карты');
        };
        document.getElementsByTagName('head')[0].appendChild(nodeScript);
      }
    });
  }

  getApiKey(): string {
    const w = this.window.nativeWindow;
    return (
      w?.app?.maps?.google?.key ||
      w?.app?.googleMapApiToken ||
      w?.angularParams?.maps?.google?.key ||
      w?.angularParams?.googleMapApiToken ||
      ''
    );
  }
}
